import { Alert, Button, Card, Popconfirm, Space, Table, Tag, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Period = {
  id: string;
  code: string;
  name: string;
  sequence_no: number;
  is_break?: number | boolean;
};

type Slot = {
  id: string;
  day_of_week: number;
  period_id: string;
  subject_code?: string;
  subject_name?: string;
  room_code?: string;
  first_name?: string;
  last_name?: string;
};

type GridDetail = {
  grid: {
    id: string;
    status: string;
    section_name?: string;
    session_name?: string;
    session_id: string;
    section_id: string;
  };
  periods: Period[];
  slots: Slot[];
};

const DAYS = [1, 2, 3, 4, 5, 6];

export default function TimetableGridDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.timetable.manage') || user?.permissions?.includes('education.admin')
  );

  const detail = useQuery({
    queryKey: ['education-timetable-grid', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await api.get<GridDetail>(`/education/timetable/grids/${id}`);
      return res.data;
    },
  });

  const publish = useMutation({
    mutationFn: () => api.post(`/education/timetable/grids/${id}/publish`),
    onSuccess: () => {
      message.success(t('timetablePublished'));
      queryClient.invalidateQueries({ queryKey: ['education-timetable-grid', id] });
      queryClient.invalidateQueries({ queryKey: ['education-timetable-grids'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const unpublish = useMutation({
    mutationFn: () => api.post(`/education/timetable/grids/${id}/unpublish`),
    onSuccess: () => {
      message.success(t('timetableUnpublished'));
      queryClient.invalidateQueries({ queryKey: ['education-timetable-grid', id] });
      queryClient.invalidateQueries({ queryKey: ['education-timetable-grids'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const deleteSlot = useMutation({
    mutationFn: (slotId: string) => api.delete(`/education/timetable/grids/${id}/slots/${slotId}`),
    onSuccess: () => {
      message.success(t('slotDeleted'));
      queryClient.invalidateQueries({ queryKey: ['education-timetable-grid', id] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const slotMap = useMemo(() => {
    const map = new Map<string, Slot>();
    for (const slot of detail.data?.slots ?? []) {
      map.set(`${slot.day_of_week}:${slot.period_id}`, slot);
    }
    return map;
  }, [detail.data?.slots]);

  if (detail.isError) {
    return (
      <EducationPageChrome title={t('timetableGrids')}>
        <Alert type="error" showIcon message={t('unavailable', { status: 404 })} />
      </EducationPageChrome>
    );
  }

  const grid = detail.data?.grid;
  const periods = (detail.data?.periods ?? []).filter((p) => !(p.is_break === 1 || p.is_break === true));
  const published = grid?.status === 'published';
  const dayLabel = (d: number) => t(`weekday${d}` as 'weekday1');

  return (
    <EducationPageChrome
      title={grid ? `${t('timetable')}: ${grid.section_name || ''}` : t('timetableGrids')}
      description={grid ? `${grid.session_name || ''} · ${grid.status}` : t('timetableGridsIntro')}
      extra={
        <Space wrap>
          {canManage && grid && !published ? (
            <Button type="primary" loading={publish.isPending} onClick={() => publish.mutate()}>
              {t('publishTimetable')}
            </Button>
          ) : null}
          {canManage && published ? (
            <Popconfirm title={t('unpublishTimetableConfirm')} onConfirm={() => unpublish.mutate()}>
              <Button loading={unpublish.isPending}>{t('unpublishTimetable')}</Button>
            </Popconfirm>
          ) : null}
          <Button onClick={() => navigate('/education/timetable/grids')}>{t('backToGrids')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {published ? <Alert type="info" showIcon message={t('timetablePublishedLocked')} /> : null}
        <Card size="small" loading={detail.isLoading}>
          <Table
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={periods}
            scroll={{ x: true }}
            columns={[
              {
                title: t('period'),
                fixed: 'left',
                width: 140,
                render: (_: unknown, row: Period) => `${row.code} — ${row.name}`,
              },
              ...DAYS.map((day) => ({
                title: dayLabel(day),
                width: 160,
                render: (_: unknown, period: Period) => {
                  const slot = slotMap.get(`${day}:${period.id}`);
                  if (!slot) {
                    return canManage && !published ? (
                      <Button
                        size="small"
                        type="link"
                        onClick={() =>
                          navigate(
                            `/education/timetable/grids/${id}/slots/new?day=${day}&period_id=${period.id}`
                          )
                        }
                      >
                        {t('addSlot')}
                      </Button>
                    ) : (
                      <Typography.Text type="secondary">—</Typography.Text>
                    );
                  }
                  const teacher = [slot.first_name, slot.last_name].filter(Boolean).join(' ');
                  return (
                    <Space direction="vertical" size={0}>
                      <Typography.Text>
                        {slot.subject_code} {slot.subject_name ? `— ${slot.subject_name}` : ''}
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {[teacher || null, slot.room_code || null].filter(Boolean).join(' · ') || '—'}
                      </Typography.Text>
                      {canManage && !published ? (
                        <Space size="small">
                          <Button
                            size="small"
                            type="link"
                            onClick={() => navigate(`/education/timetable/grids/${id}/slots/${slot.id}/edit`)}
                          >
                            {t('edit')}
                          </Button>
                          <Popconfirm title={t('slotDeleteConfirm')} onConfirm={() => deleteSlot.mutate(slot.id)}>
                            <Button size="small" type="link" danger>
                              {t('remove')}
                            </Button>
                          </Popconfirm>
                        </Space>
                      ) : null}
                    </Space>
                  );
                },
              })),
            ]}
          />
        </Card>
        {grid ? (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            <Tag color={published ? 'success' : 'default'}>{grid.status}</Tag>
            {t('slots')}: {detail.data?.slots?.length ?? 0}
          </Typography.Paragraph>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
